import { useState, useEffect } from "react";
import { X, ChevronLeft, ChevronRight, Stethoscope, Calendar, Clock, Settings, TrendingUp, Users, Save, MousePointer } from "lucide-react";
import { Button } from "./ui/button";
import { useTheme } from "@/contexts/ThemeContext";

const slides = [
  {
    id: "welcome",
    title: "Welcome to Doc RVU Tracker!",
    subtitle: "Your personal On-Shift RVU & Patient Tracking tool",
    icon: Stethoscope,
    iconColor: "text-emerald-400",
    content: [
      "Track patient encounters with One Click",
      "Calculate wRVUs and earnings in real-time",
      "View monthly summaries and shift history",
      "Export your RVU & Patient Visits to Excel/CSV File"
    ],
    tip: "Let's take a quick tour of the main features!"
  },
  {
    id: "tracker",
    title: "Tracker Tab",
    subtitle: "Your daily shift entry hub",
    icon: MousePointer,
    iconColor: "text-blue-400",
    visual: "tracker",
    content: [
      "Select the date and enter your clinic location",
      "Customize your six Quick-Add Visits under Settings (your most commonly used codes)",
      { type: "buttons", text: "Use %GREEN_PLUS% and %RED_MINUS% for one-click tracking — perfect for use during your shift" },
      "Your shift auto-saves as you make changes"
    ],
    tip: "Pro tip: Use the CPT search tool near the bottom of the page to look up any CPT code and add directly to your Tracker!"
  },
  {
    id: "cpt-reference",
    title: "Quick-Add CPT List",
    subtitle: "Your custom list of other visit and procedure/service codes",
    icon: Stethoscope,
    iconColor: "text-teal-400",
    visual: "cpt-reference",
    content: [
      "Click the Quick-Add CPT List tab to open panel",
      "Build your list of commonly-used Visit Types & Procedure/Service Codes",
      "Right-click any code to instantly add it to your Tracker (Long-press on mobile browser)",
      "Drag and drop codes to reorder your personal reference list"
    ],
    tip: "Pro tip: Press C on your keyboard to quickly open & close the CPT Ref List!"
  },
  {
    id: "calendar",
    title: "Calendar Tab",
    subtitle: "Monthly overview at a glance",
    icon: Calendar,
    iconColor: "text-purple-400",
    visual: "calendar",
    content: [
      "View all your shifts for the month in calendar format",
      "Each day shows patient count, wRVUs, and earnings",
      "Monthly totals are displayed at the top",
      "Use arrows to navigate between months"
    ],
    tip: "Pro tip: Click on any day to jump to that shift in the Tracker!"
  },
  {
    id: "history",
    title: "History Tab",
    subtitle: "Detailed shift records",
    icon: Clock,
    iconColor: "text-amber-400",
    visual: "history",
    content: [
      "View a chronological list of all your saved shifts",
      "Click on any shift to expand and see full details",
      "See breakdowns by visit type, procedures & services, and hourly earnings",
      "Edit or delete shifts as needed",
      "Export shift data as an Excel-compatible file for records and accounting"
    ],
    tip: "Pro tip: Shift data is exportable by month, quarter, or by custom date range!"
  },
  {
    id: "settings",
    title: "Settings Tab",
    subtitle: "Customize your experience",
    icon: Settings,
    iconColor: "text-rose-400",
    visual: "settings",
    content: [
      "Set your tax rate and $/wRVU rate for accurate calculations",
      "Customize the Quick-Add Visits to your 6 most common visit types",
      "Manage your list of clinic locations & practice settings",
      "Export your shift data to CSV for any date range",
      "Access this tutorial anytime from the Help button"
    ],
    tip: "Pro tip: You can customize the color and order of your Quick-Add Visits!"
  },
  {
    id: "tips",
    title: "Important Tips",
    subtitle: "Get the most out of Doc RVU Tracker",
    icon: TrendingUp,
    iconColor: "text-cyan-400",
    content: [
      "Auto-save: Changes on the Tracker page save automatically as you go",
      "On-Shift use: Keep tracker open and tally patient visits as you work",
      "Multi-device: Perfect for a second tab, screen, or even mobile browser",
      "Data privacy: Your data is private and only visible to you",
      "Theme options: Toggle light and dark mode to fit your visual preference"
    ],
    tip: "You're all set! Start tracking your shifts and watch your productivity grow!"
  }
];

// Visual illustrations for each tab - theme-aware
const TabVisual = ({ type, isDark }) => {
  if (type === "tracker") {
    return (
      <div className={`rounded-lg p-4 border ${
        isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-gray-100 border-gray-300'
      }`}>
        <div className="flex items-center gap-2 mb-3">
          <div className={`w-20 h-8 rounded flex items-center justify-center text-xs ${
            isDark ? 'bg-slate-700 text-slate-400' : 'bg-white text-gray-500 border border-gray-300'
          }`}>Date</div>
          <div className={`w-32 h-8 rounded flex items-center justify-center text-xs ${
            isDark ? 'bg-slate-700 text-slate-400' : 'bg-white text-gray-500 border border-gray-300'
          }`}>Clinic Location</div>
        </div>
        <div className="grid grid-cols-3 gap-2 mb-3">
          {[
            { code: "99213", desc: "Est. Patient, Lvl 3", wrvu: "1.30" },
            { code: "99214", desc: "Est. Patient, Lvl 4", wrvu: "1.92" },
            { code: "99215", desc: "Est. Patient, Lvl 5", wrvu: "2.80" }
          ].map((item, i) => (
            <div key={item.code} className={`p-2 rounded border ${
              i === 0 
                ? 'border-blue-500 bg-blue-500/10' 
                : isDark ? 'border-slate-600 bg-slate-700/50' : 'border-gray-300 bg-white'
            }`}>
              <div className={`text-sm font-mono font-semibold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>{item.code}</div>
              <div className={`text-[10px] truncate ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{item.desc}</div>
              <div className={`text-[10px] ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`}>{item.wrvu} wRVU</div>
              <div className="flex items-center justify-between mt-1">
                <div className="flex items-center gap-1">
                  <div className="w-5 h-5 bg-red-500/20 rounded text-xs flex items-center justify-center text-red-400">-</div>
                  <span className={`text-lg font-bold ${isDark ? 'text-slate-300' : 'text-gray-800'}`}>{i + 2}</span>
                  <div className="w-5 h-5 bg-emerald-500/20 rounded text-xs flex items-center justify-center text-emerald-400">+</div>
                </div>
              </div>
            </div>
          ))}
        </div>
        {/* Totals bar styled like the actual TotalsBar component */}
        <div className={`rounded-lg p-3 border-t ${
          isDark ? 'bg-slate-900/80 border-slate-600' : 'bg-white border-gray-300 shadow-sm'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                isDark ? 'bg-blue-500/15' : 'bg-blue-100'
              }`}>
                <span className={`text-sm ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>👥</span>
              </div>
              <div>
                <div className={`text-[10px] uppercase tracking-wide ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Patients</div>
                <div className={`text-base font-mono font-bold ${isDark ? 'text-slate-100' : 'text-gray-900'}`}>9</div>
              </div>
            </div>
            <div className={`w-px h-8 ${isDark ? 'bg-slate-700' : 'bg-gray-300'}`} />
            <div className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                isDark ? 'bg-emerald-500/15' : 'bg-emerald-100'
              }`}>
                <span className={`text-sm ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>📈</span>
              </div>
              <div>
                <div className={`text-[10px] uppercase tracking-wide ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>wRVUs</div>
                <div className={`text-base font-mono font-bold ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>15.2</div>
              </div>
            </div>
            <div className={`w-px h-8 ${isDark ? 'bg-slate-700' : 'bg-gray-300'}`} />
            <div className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                isDark ? 'bg-amber-500/15' : 'bg-amber-100'
              }`}>
                <span className={`text-sm ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>$</span>
              </div>
              <div>
                <div className={`text-[10px] uppercase tracking-wide ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Pre-Tax</div>
                <div className={`text-base font-mono font-bold ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>$380</div>
              </div>
            </div>
            <div className={`w-px h-8 ${isDark ? 'bg-slate-700' : 'bg-gray-300'}`} />
            <div className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                isDark ? 'bg-sky-500/15' : 'bg-sky-100'
              }`}>
                <span className={`text-sm ${isDark ? 'text-sky-400' : 'text-sky-600'}`}>$</span>
              </div>
              <div>
                <div className={`text-[10px] uppercase tracking-wide ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Post-Tax</div>
                <div className={`text-base font-mono font-bold ${isDark ? 'text-sky-400' : 'text-sky-600'}`}>$289</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  if (type === "cpt-reference") {
    return (
      <div className={`rounded-lg p-4 border ${
        isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-gray-100 border-gray-300'
      }`}>
        <div className="flex gap-3">
          {/* Left side - CPT Ref panel */}
          <div className={`w-44 border rounded-lg p-2 ${
            isDark ? 'border-teal-500/50 bg-slate-900/50' : 'border-teal-400 bg-white'
          }`}>
            <div className={`text-xs font-medium mb-2 flex items-center gap-1 ${
              isDark ? 'text-teal-400' : 'text-teal-600'
            }`}>
              <span>📋</span> Quick-Add CPT List
            </div>
            {/* Code list */}
            <div className="space-y-1">
              {[
                { code: "99213", desc: "Est. Patient, Lvl 3", wrvu: "1.30" },
                { code: "99214", desc: "Est. Patient, Lvl 4", wrvu: "1.92" },
                { code: "69210", desc: "Cerumen removal", wrvu: "0.59" },
              ].map((item, i) => (
                <div 
                  key={item.code} 
                  className={`p-1.5 rounded text-xs ${
                    i === 1 
                      ? isDark 
                        ? 'bg-teal-500/20 border border-teal-500/50 ring-1 ring-teal-400' 
                        : 'bg-teal-100 border border-teal-400 ring-1 ring-teal-400'
                      : isDark ? 'bg-slate-800' : 'bg-gray-50'
                  }`}
                >
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-1">
                      <span className={isDark ? 'text-slate-500' : 'text-gray-400'}>⋮⋮</span>
                      <span className={`font-mono ${isDark ? 'text-teal-400' : 'text-teal-600'}`}>{item.code}</span>
                    </div>
                    <span className={isDark ? 'text-cyan-400' : 'text-cyan-600'}>{item.wrvu}</span>
                  </div>
                  <div className={`text-[10px] truncate ml-4 ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>{item.desc}</div>
                </div>
              ))}
            </div>
            {/* Right-click hint */}
            <div className={`mt-2 p-1.5 rounded text-[10px] text-center border ${
              isDark ? 'bg-yellow-500/10 border-yellow-500/30 text-yellow-400' : 'bg-yellow-50 border-yellow-300 text-yellow-700'
            }`}>
              💡 Right-click to add to Tracker
            </div>
          </div>
          
          {/* Right side - accurate context menu mockup */}
          <div className="flex-1 flex flex-col justify-center">
            <div className={`text-xs mb-2 text-center ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Right-click menu:</div>
            <div className={`border rounded-lg shadow-xl w-48 mx-auto overflow-hidden ${
              isDark ? 'bg-slate-800 border-slate-600' : 'bg-white border-gray-300'
            }`}>
              {/* Menu header with code info */}
              <div className={`px-3 py-2 border-b ${
                isDark ? 'border-slate-700 bg-slate-900/50' : 'border-gray-200 bg-gray-50'
              }`}>
                <p className={`text-xs font-mono font-bold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>99214</p>
                <p className={`text-[10px] truncate ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Est. Patient, Lvl 4</p>
              </div>
              {/* Menu options */}
              <div className="py-1">
                <div className={`flex items-center gap-2 px-3 py-2 text-xs ${
                  isDark ? 'text-slate-200 hover:bg-purple-500/20' : 'text-gray-700 hover:bg-purple-50'
                } cursor-pointer`}>
                  <span className="text-purple-400">👥</span>
                  Add to Other Patient Visits
                </div>
                <div className={`flex items-center gap-2 px-3 py-2 text-xs cursor-pointer ${
                  isDark ? 'text-slate-200 bg-teal-500/20' : 'text-gray-700 bg-teal-50'
                }`}>
                  <span className="text-teal-400">🩺</span>
                  Add to Procedures & Services
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (type === "calendar") {
    // Sample shift data - 9 shifts spread out, no Sundays
    const shiftDays = {
      2: { clinic: "Main Clinic", patients: 14, wrvus: "21.80", preTax: "$1,635" },
      3: { clinic: "Downtown", patients: 16, wrvus: "24.50", preTax: "$1,838" },
      5: { clinic: "Main Clinic", patients: 18, wrvus: "27.20", preTax: "$2,040" },
      6: { clinic: "Westside", patients: 12, wrvus: "18.60", preTax: "$1,395" },
      7: { clinic: "Main Clinic", patients: 15, wrvus: "23.10", preTax: "$1,733" },
      9: { clinic: "Downtown", patients: 19, wrvus: "29.40", preTax: "$2,205" },
      11: { clinic: "Westside", patients: 17, wrvus: "26.30", preTax: "$1,973" },
      12: { clinic: "Main Clinic", patients: 14, wrvus: "21.50", preTax: "$1,613" },
      14: { clinic: "Downtown", patients: 16, wrvus: "25.20", preTax: "$1,890" },
    };
    
    // Totals: 141 patients, 217.60 wRVUs, $17,322 pre-tax, ~$13,165 post-tax
    
    return (
      <div className={`rounded-lg p-3 border ${
        isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-gray-100 border-gray-300'
      }`}>
        {/* Stats cards row */}
        <div className="grid grid-cols-4 gap-2 mb-3">
          {[
            { label: "PATIENTS", value: "141", color: "blue" },
            { label: "WRVUS", value: "217.60", color: "emerald" },
            { label: "PRE-TAX $", value: "$17,322", color: "amber" },
            { label: "POST-TAX $", value: "$13,165", color: "sky" },
          ].map((stat) => (
            <div key={stat.label} className={`p-2 rounded-lg border ${
              isDark ? 'bg-slate-900/50 border-slate-700' : 'bg-white border-gray-200'
            }`}>
              <div className={`text-[8px] uppercase tracking-wide ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>{stat.label}</div>
              <div className={`text-sm font-mono font-bold ${
                stat.color === 'blue' ? (isDark ? 'text-blue-400' : 'text-blue-600') :
                stat.color === 'emerald' ? (isDark ? 'text-emerald-400' : 'text-emerald-600') :
                stat.color === 'amber' ? (isDark ? 'text-amber-400' : 'text-amber-600') :
                (isDark ? 'text-sky-400' : 'text-sky-600')
              }`}>{stat.value}</div>
            </div>
          ))}
        </div>
        
        {/* Month header */}
        <div className={`flex items-center justify-center gap-3 mb-2 pb-2 border-b ${
          isDark ? 'border-slate-700' : 'border-gray-300'
        }`}>
          <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>◀</span>
          <span className={`text-sm font-semibold ${isDark ? 'text-slate-200' : 'text-gray-800'}`}>March 2026</span>
          <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>▶</span>
        </div>
        
        {/* Calendar grid */}
        <div className="grid grid-cols-7 gap-0.5 text-[10px]">
          {["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"].map((d) => (
            <div key={d} className={`text-center py-1 font-medium ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>{d}</div>
          ))}
          {[...Array(14)].map((_, i) => {
            const day = i + 1;
            const shift = shiftDays[day];
            const isToday = day === 5;
            return (
              <div 
                key={i} 
                className={`rounded p-1.5 min-h-[62px] ${
                  shift 
                    ? isDark 
                      ? 'bg-emerald-500/10 border border-emerald-500/30' 
                      : 'bg-emerald-50 border border-emerald-200'
                    : isToday
                      ? isDark ? 'bg-blue-500/10 border border-blue-500/30' : 'bg-blue-50 border border-blue-200'
                      : ''
                }`}
              >
                <div className={`text-xs font-bold ${
                  shift ? (isDark ? 'text-emerald-400' : 'text-emerald-700') :
                  isToday ? (isDark ? 'text-blue-400' : 'text-blue-600') :
                  (isDark ? 'text-slate-500' : 'text-gray-500')
                }`}>{day}</div>
                {shift && (
                  <div className="leading-tight mt-1 space-y-0.5">
                    <div className={`text-[9px] font-medium truncate ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>{shift.clinic}</div>
                    <div className={`text-[9px] font-semibold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>👥 {shift.patients}</div>
                    <div className={`text-[9px] font-semibold ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>📈 {shift.wrvus}</div>
                    <div className={`text-[9px] font-semibold ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>{shift.preTax}</div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }
  
  if (type === "history") {
    return (
      <div className={`rounded-lg p-4 border space-y-2 ${
        isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-gray-100 border-gray-300'
      }`}>
        {[
          { date: "Mar 1, 2026", patients: 12, wrvus: "18.4" },
          { date: "Feb 28, 2026", patients: 8, wrvus: "12.1" },
          { date: "Feb 27, 2026", patients: 15, wrvus: "22.5" }
        ].map((shift, i) => (
          <div key={i} className={`p-2 rounded border ${
            i === 0 
              ? isDark 
                ? 'border-slate-500 bg-slate-700/50' 
                : 'border-gray-400 bg-white'
              : isDark ? 'border-slate-700' : 'border-gray-300 bg-white/50'
          }`}>
            <div className="flex justify-between items-center">
              <span className={`text-sm ${isDark ? 'text-slate-300' : 'text-gray-800'}`}>{shift.date}</span>
              <div className="flex gap-3 text-xs">
                <span className={isDark ? 'text-slate-400' : 'text-gray-600'}>{shift.patients} pts</span>
                <span className={isDark ? 'text-cyan-400' : 'text-cyan-600'}>{shift.wrvus} wRVUs</span>
              </div>
            </div>
            {i === 0 && (
              <div className={`mt-2 pt-2 border-t text-xs ${
                isDark ? 'border-slate-600 text-slate-500' : 'border-gray-300 text-gray-500'
              }`}>
                Click to expand and see full breakdown...
              </div>
            )}
          </div>
        ))}
      </div>
    );
  }
  
  if (type === "settings") {
    return (
      <div className={`rounded-lg p-3 border space-y-3 ${
        isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-gray-100 border-gray-300'
      }`}>
        {/* Rate configuration cards */}
        <div className="grid grid-cols-2 gap-2">
          {/* Tax Rate Card */}
          <div className={`p-2 rounded-lg border ${
            isDark ? 'bg-slate-900/50 border-slate-700' : 'bg-white border-gray-200'
          }`}>
            <div className={`text-[9px] uppercase tracking-wide flex items-center gap-1 ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
              <span>⚙️</span> Tax Rate (2026)
            </div>
            <div className="flex items-center justify-between mt-1">
              <span className={`text-lg font-bold ${isDark ? 'text-slate-200' : 'text-gray-800'}`}>24%</span>
              <button className={`text-[9px] px-2 py-1 rounded flex items-center gap-1 ${
                isDark ? 'bg-slate-700 text-slate-300' : 'bg-gray-100 text-gray-600 border border-gray-300'
              }`}>
                ✏️ Change
              </button>
            </div>
          </div>
          {/* wRVU Rate Card */}
          <div className={`p-2 rounded-lg border ${
            isDark ? 'bg-slate-900/50 border-slate-700' : 'bg-white border-gray-200'
          }`}>
            <div className={`text-[9px] uppercase tracking-wide flex items-center gap-1 ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
              <span>💰</span> wRVU Rate (2026)
            </div>
            <div className="flex items-center justify-between mt-1">
              <span className={`text-lg font-bold ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>$50/wRVU</span>
              <button className={`text-[9px] px-2 py-1 rounded flex items-center gap-1 ${
                isDark ? 'bg-slate-700 text-slate-300' : 'bg-gray-100 text-gray-600 border border-gray-300'
              }`}>
                ✏️ Change
              </button>
            </div>
          </div>
        </div>
        
        {/* Quick-Add Visit Types */}
        <div className={`pt-3 border-t ${isDark ? 'border-slate-700' : 'border-gray-300'}`}>
          <div className={`text-xs font-semibold mb-3 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>Quick-Add Visit Types</div>
          <div className="grid grid-cols-6 gap-1.5">
            {[
              { code: "99213", color: "blue" },
              { code: "99214", color: "green" },
              { code: "99215", color: "orange" },
              { code: "99395", color: "red" },
              { code: "99396", color: "yellow" },
              { code: "99397", color: "pink" }
            ].map((item) => (
              <div 
                key={item.code} 
                className={`py-2 px-1 rounded text-xs text-center font-mono font-bold
                  ${item.color === 'blue' ? 'bg-blue-500/30 text-blue-400' : ''}
                  ${item.color === 'green' ? 'bg-emerald-500/30 text-emerald-400' : ''}
                  ${item.color === 'orange' ? 'bg-orange-500/30 text-orange-400' : ''}
                  ${item.color === 'red' ? 'bg-red-500/30 text-red-400' : ''}
                  ${item.color === 'yellow' ? 'bg-yellow-500/30 text-yellow-500' : ''}
                  ${item.color === 'pink' ? 'bg-pink-500/30 text-pink-400' : ''}
                `}
              >
                {item.code}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }
  
  return null;
};

export default function OnboardingTutorial({ isOpen, onClose, onComplete }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [currentSlide, setCurrentSlide] = useState(0);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  
  // Reset to first slide and checkbox when tutorial opens
  useEffect(() => {
    if (isOpen) {
      setCurrentSlide(0);
      setDontShowAgain(false);
    }
  }, [isOpen]);
  
  if (!isOpen) return null;
  
  const slide = slides[currentSlide];
  const isFirst = currentSlide === 0;
  const isLast = currentSlide === slides.length - 1;
  const IconComponent = slide.icon;
  
  const handleNext = () => {
    if (isLast) {
      onComplete(dontShowAgain);
    } else {
      setCurrentSlide(prev => prev + 1);
    }
  };
  
  const handlePrev = () => {
    if (!isFirst) {
      setCurrentSlide(prev => prev - 1);
    }
  };
  
  const handleSkip = () => {
    onComplete(dontShowAgain);
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
      <div className={`relative w-full max-w-2xl mx-4 border rounded-2xl shadow-2xl overflow-hidden ${
        isDark ? 'bg-slate-900 border-slate-700' : 'bg-white border-gray-300'
      }`}>
        {/* Header */}
        <div className={`flex items-center justify-between p-4 border-b ${
          isDark ? 'border-slate-700' : 'border-gray-200'
        }`}>
          <div className="flex items-center gap-2">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentSlide(i)}
                className={`w-2 h-2 rounded-full transition-all ${
                  i === currentSlide 
                    ? 'w-6 bg-emerald-500' 
                    : i < currentSlide 
                      ? 'bg-emerald-500/50' 
                      : isDark ? 'bg-slate-600' : 'bg-gray-300'
                }`}
              />
            ))}
          </div>
          <button
            data-testid="tutorial-close-btn"
            onClick={handleSkip}
            className={`transition-colors ${isDark ? 'text-slate-400 hover:text-slate-200' : 'text-gray-400 hover:text-gray-600'}`}
          >
            <X size={20} />
          </button>
        </div>
        
        {/* Content */}
        <div className="p-6">
          {/* Icon and Title */}
          <div className="flex items-center gap-4 mb-6">
            <div className={`p-3 rounded-xl ${slide.iconColor} ${isDark ? 'bg-slate-800' : 'bg-gray-100'}`}>
              <IconComponent size={32} />
            </div>
            <div>
              <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{slide.title}</h2>
              <p className={isDark ? 'text-slate-400' : 'text-gray-600'}>{slide.subtitle}</p>
            </div>
          </div>
          
          {/* Visual */}
          {slide.visual && (
            <div className="mb-6">
              <TabVisual type={slide.visual} isDark={isDark} />
            </div>
          )}
          
          {/* Content Points */}
          <ul className="space-y-3 mb-6">
            {slide.content.map((point, i) => {
              // Handle special button formatting
              let content;
              if (typeof point === 'object' && point.type === 'buttons') {
                const parts = point.text.split(/(%RED_MINUS%|%GREEN_PLUS%)/);
                content = (
                  <span className={isDark ? 'text-slate-300' : 'text-gray-700'}>
                    {parts.map((part, idx) => {
                      if (part === '%RED_MINUS%') {
                        return (
                          <span key={idx} className="inline-flex items-center justify-center w-5 h-5 mx-0.5 bg-red-500/20 rounded text-xs text-red-400 font-bold align-middle">−</span>
                        );
                      }
                      if (part === '%GREEN_PLUS%') {
                        return (
                          <span key={idx} className="inline-flex items-center justify-center w-5 h-5 mx-0.5 bg-emerald-500/20 rounded text-xs text-emerald-400 font-bold align-middle">+</span>
                        );
                      }
                      return part;
                    })}
                  </span>
                );
              } else {
                content = <span className={isDark ? 'text-slate-300' : 'text-gray-700'}>{point}</span>;
              }
              
              return (
                <li key={i} className="flex items-start gap-3">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                    isDark ? 'bg-emerald-500/20' : 'bg-emerald-100'
                  }`}>
                    <span className={`text-sm font-bold ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>{i + 1}</span>
                  </div>
                  {content}
                </li>
              );
            })}
          </ul>
          
          {/* Tip */}
          {slide.tip && (
            <div className={`p-4 rounded-lg border ${
              isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-emerald-50 border-emerald-200'
            }`}>
              <p className={`text-sm ${isDark ? 'text-emerald-400' : 'text-emerald-700'}`}>
                <span className="font-semibold">💡 </span>
                {slide.tip}
              </p>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className={`border-t ${isDark ? 'border-slate-700 bg-slate-800/30' : 'border-gray-200 bg-gray-50'}`}>
          {/* Don't Show Again checkbox and Settings note */}
          <div className="px-4 pt-3 pb-2 flex items-center justify-between">
            <label className="flex items-center gap-2 cursor-pointer group">
              <input
                type="checkbox"
                checked={dontShowAgain}
                onChange={(e) => setDontShowAgain(e.target.checked)}
                className={`w-4 h-4 rounded text-emerald-500 focus:ring-emerald-500 focus:ring-offset-0 cursor-pointer ${
                  isDark ? 'border-slate-600 bg-slate-700' : 'border-gray-400 bg-white'
                }`}
              />
              <span className={`text-sm transition-colors ${
                isDark ? 'text-slate-400 group-hover:text-slate-300' : 'text-gray-500 group-hover:text-gray-700'
              }`}>
                Don't show this again
              </span>
            </label>
            <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
              You can access this tutorial anytime from <span className={isDark ? 'text-emerald-400' : 'text-emerald-600'}>Settings</span>
            </span>
          </div>
          
          {/* Navigation buttons */}
          <div className="flex items-center justify-between p-4 pt-2">
            <Button
              data-testid="tutorial-back-btn"
              variant="ghost"
              onClick={handlePrev}
              disabled={isFirst}
              className={`disabled:opacity-30 ${
                isDark ? 'text-slate-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'
              }`}
            >
              <ChevronLeft size={20} className="mr-1" />
              Back
            </Button>
            
            <span className={`text-sm ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
              {currentSlide + 1} of {slides.length}
            </span>
            
            <Button
              data-testid="tutorial-next-btn"
              onClick={handleNext}
              className="bg-emerald-600 hover:bg-emerald-500 text-white"
            >
              {isLast ? "Get Started" : "Next"}
              {!isLast && <ChevronRight size={20} className="ml-1" />}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
